import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Award, Briefcase, Lightbulb, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import StarRating from "@/components/StarRating";
import api, { formatApiError } from "@/lib/api";
import { TRAITS, LEADERSHIP_TRAITS, MANAGEMENT_TRAITS } from "@/lib/traits";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const emptyTraits = TRAITS.reduce((a, t) => ({ ...a, [t.key]: 0 }), {});

function TraitRow({ t, value, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <t.icon size={17} className="text-emerald-600" />
        </div>
        <div>
          <div className="font-semibold text-slate-800">{t.label}</div>
          <div className="text-xs text-slate-400">{t.description}</div>
        </div>
      </div>
      <StarRating testid={`trait-rating-${t.testid}`} value={value} onChange={onChange} size={24} />
    </div>
  );
}

export default function WriteReview() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const isEdit = !!editId;
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [sup, setSup] = useState(null);
  const [traits, setTraits] = useState(emptyTraits);
  const [overall, setOverall] = useState(0);
  const [form, setForm] = useState({
    title: "", comment: "", pros: "", cons: "", advice: "",
    leadership_improvement: "", management_improvement: "",
    reviewer_role: "", reviewer_location: "", tenure: "",
  });
  const [recommend, setRecommend] = useState(true);
  const [hideText, setHideText] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagOptions, setTagOptions] = useState({ tags: [], max: 3 });
  useEffect(() => { api.get("/tags").then((r) => setTagOptions(r.data)); }, []);
  const toggleTag = (t) => setTags((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : ts.length < tagOptions.max ? [...ts, t] : ts));
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === false) {
      setAuthOpen(true);
      navigate(`/supervisor/${id}`);
    }
  }, [user]); // eslint-disable-line

  useEffect(() => {
    api.get(`/supervisors/${id}`).then((r) => setSup(r.data));
  }, [id]);

  useEffect(() => {
    if (isEdit && user) {
      api.get(`/reviews/${editId}`).then((r) => {
        const d = r.data;
        setTraits({ ...emptyTraits, ...(d.traits || {}) });
        setOverall(d.overall || 0);
        setRecommend(d.would_recommend ?? true);
        setHideText(!!d.hide_text);
        setTags(d.tags || []);
        setAgreed(true);
        setForm({
          title: d.title || "", comment: d.comment || "", pros: d.pros || "", cons: d.cons || "",
          advice: d.advice || "", leadership_improvement: d.leadership_improvement || "",
          management_improvement: d.management_improvement || "",
          reviewer_role: d.reviewer_role || "", reviewer_location: d.reviewer_location || "",
          tenure: d.tenure || "",
        });
      }).catch(() => {
        toast.error("Couldn't load your review to edit");
        navigate(`/supervisor/${id}`);
      });
    }
  }, [isEdit, editId, user]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTrait = (k, v) => setTraits((t) => ({ ...t, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (Object.values(traits).some((v) => v === 0)) { setError("Please rate all leadership and management traits."); return; }
    if (overall === 0) { setError("Please provide an overall rating."); return; }
    if (!agreed) { setError("Please acknowledge the community guidelines & disclaimer."); return; }
    setLoading(true);
    try {
      const payload = { traits, overall, would_recommend: recommend, hide_text: hideText, tags, ...form };
      if (isEdit) {
        await api.put(`/reviews/${editId}`, payload);
        toast.success("Review updated");
      } else {
        const { data } = await api.post("/reviews", { supervisor_id: id, ...payload });
        if (data.ratings_hidden) {
          toast.success(`Review saved privately — it publishes once ${data.min_reviews} employees have reviewed ${sup.name}.`, { duration: 7000 });
        } else {
          toast.success("Review submitted — thank you!");
        }
      }
      navigate(`/supervisor/${id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!sup) return <div className="max-w-3xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate(`/supervisor/${id}`)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6">
        <ArrowLeft size={16} /> Back to profile
      </button>

      <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">{isEdit ? "Edit your review" : "Anonymous review"}</p>
      <h1 className="font-display text-4xl sm:text-5xl text-slate-900 mb-1" style={{ fontWeight: 900 }}>
        {isEdit ? "Editing your review of" : "Reviewing"} {sup.name}
      </h1>
      <p className="text-slate-500 mb-8">{sup.title} · {sup.company}</p>

      <form onSubmit={submit} className="space-y-8">
        {error && (
          <div data-testid="review-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
        )}

        {sup.ratings_hidden && !isEdit && (
          <div data-testid="anonymity-warning" className="bg-slate-900 text-white rounded-xl p-5 flex items-start gap-3 lb-grain">
            <Lock size={20} className="text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-emerald-300">Small-team anonymity protection is on</p>
              <p className="text-slate-300 mt-1">
                {sup.name} has fewer than {sup.min_reviews} reviews, so your ratings and comments stay hidden from everyone (including {sup.name}) until {sup.min_reviews} employees have reviewed them.
                To stay anonymous, avoid mentioning specific events, dates, projects, or details only you would know.
              </p>
            </div>
          </div>
        )}

        {/* Leadership skills */}
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Award size={20} className="text-emerald-600" />
            <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 700 }}>Leadership skills</h2>
          </div>
          <p className="text-sm text-slate-400 mb-5">How they lead, inspire, and treat the people around them.</p>
          <div className="space-y-5">
            {LEADERSHIP_TRAITS.map((t) => (
              <TraitRow key={t.key} t={t} value={traits[t.key]} onChange={(v) => setTrait(t.key, v)} />
            ))}
          </div>
          <div className="mt-5">
            <Label className="flex items-center gap-1.5"><Lightbulb size={14} className="text-amber-500" /> What could they do to improve as a leader?</Label>
            <Textarea data-testid="leadership-improvement-input" rows={3} value={form.leadership_improvement} onChange={(e) => set("leadership_improvement", e.target.value)} placeholder="Constructive suggestions on their leadership..." />
          </div>
        </section>

        {/* Management skills */}
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase size={20} className="text-emerald-600" />
            <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 700 }}>Management skills</h2>
          </div>
          <p className="text-sm text-slate-400 mb-5">How they run the day-to-day: workflow, delegation, and the team.</p>
          <div className="space-y-5">
            {MANAGEMENT_TRAITS.map((t) => (
              <TraitRow key={t.key} t={t} value={traits[t.key]} onChange={(v) => setTrait(t.key, v)} />
            ))}
          </div>
          <div className="mt-5">
            <Label className="flex items-center gap-1.5"><Lightbulb size={14} className="text-amber-500" /> What could they do to improve at management?</Label>
            <Textarea data-testid="management-improvement-input" rows={3} value={form.management_improvement} onChange={(e) => set("management_improvement", e.target.value)} placeholder="Constructive suggestions on their management..." />
          </div>
        </section>

        {/* Overall */}
        <section className="bg-slate-900 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-3 lb-grain">
          <div>
            <h2 className="font-display text-2xl text-white" style={{ fontWeight: 700 }}>Overall rating</h2>
            <p className="text-slate-400 text-sm">How would you rate this supervisor overall?</p>
          </div>
          <StarRating testid="overall-rating" value={overall} onChange={setOverall} size={34} />
        </section>

        {/* Written */}
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 700 }}>Your experience</h2>
          <div>
            <Label>Review title</Label>
            <Input data-testid="review-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Summarize your experience" />
          </div>
          <div>
            <Label>Detailed feedback</Label>
            <Textarea data-testid="review-comment-input" required rows={4} value={form.comment} onChange={(e) => set("comment", e.target.value)} placeholder="Describe what it's like to work under this supervisor..." />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-emerald-700">Pros</Label>
              <Textarea data-testid="review-pros-input" rows={3} value={form.pros} onChange={(e) => set("pros", e.target.value)} placeholder="What they do well" />
            </div>
            <div>
              <Label className="text-rose-600">Cons</Label>
              <Textarea data-testid="review-cons-input" rows={3} value={form.cons} onChange={(e) => set("cons", e.target.value)} placeholder="Where they could improve" />
            </div>
          </div>
          <div>
            <Label>Advice to management (optional)</Label>
            <Input data-testid="review-advice-input" value={form.advice} onChange={(e) => set("advice", e.target.value)} placeholder="Constructive suggestion" />
          </div>
        </section>

        {/* Anonymity meta */}
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 700 }}>Anonymous context</h2>
          <p className="text-sm text-slate-500 -mt-2">Shown on your review without revealing your identity.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Your role</Label>
              <Input data-testid="review-role-input" value={form.reviewer_role} onChange={(e) => set("reviewer_role", e.target.value)} placeholder="e.g. Engineer" />
            </div>
            <div>
              <Label>Location</Label>
              <Input data-testid="review-location-input" value={form.reviewer_location} onChange={(e) => set("reviewer_location", e.target.value)} placeholder="e.g. Remote" />
            </div>
            <div>
              <Label>Tenure</Label>
              <Input data-testid="review-tenure-input" value={form.tenure} onChange={(e) => set("tenure", e.target.value)} placeholder="e.g. 2 years" />
            </div>
          </div>
          <div>
            <Label>Behavior flags <span className="text-slate-400 font-normal">(optional, up to {tagOptions.max})</span></Label>
            <p className="text-xs text-slate-500 mb-2">Patterns you experienced repeatedly. These are counted across reviews — never tied to you.</p>
            <div className="flex flex-wrap gap-2" data-testid="review-tags">
              {tagOptions.tags.map((t) => (
                <button type="button" key={t} data-testid={`review-tag-${t.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleTag(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tags.includes(t) ? "bg-rose-500 text-white border-rose-500" : "border-slate-300 text-slate-600 hover:border-rose-400"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox data-testid="review-recommend-checkbox" checked={recommend} onCheckedChange={setRecommend} />
            <span className="text-sm text-slate-600">I would recommend working under this supervisor.</span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Checkbox data-testid="review-hide-text-checkbox" checked={hideText} onCheckedChange={setHideText} className="mt-0.5" />
            <span className="text-sm text-slate-600 leading-relaxed">
              <b className="text-slate-800 flex items-center gap-1.5"><Lock size={13} className="text-emerald-600" /> Share my ratings only</b>
              Keep my written comments, pros/cons, and role details private. Only my star and trait ratings count toward the score — the safest choice on a small team.
            </span>
          </label>
        </section>

        {/* Disclaimer */}
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox data-testid="review-disclaimer-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
            <span className="text-sm text-slate-700 leading-relaxed">
              I confirm this review reflects my own honest, good-faith opinion and first-hand experience.
              I understand my360boss is an opinion platform about how supervisors manage people — not a channel for reporting safety, legal, or ethical violations (use your employer's ethics hotline or a regulator for those) — and I am solely responsible for what I post. See our{" "}
              <Link to="/disclaimer" target="_blank" className="text-emerald-700 underline font-medium">disclaimer & guidelines</Link>.
            </span>
          </label>
        </section>

        <Button data-testid="submit-review-submit-btn" type="submit" disabled={loading} className="w-full h-12 text-base bg-slate-900 hover:bg-emerald-600 rounded-full">
          {loading ? "Saving..." : isEdit ? "Save Changes" : "Submit Anonymous Review"}
        </Button>
      </form>
    </div>
  );
}
